import { ClientBusinessPartialModel } from 'app/client/clientBusiness/models/ClientBusiness';
import { useRef, useState } from 'react';
import FilterField from 'shared/components/filters/FilterField';
import FiltersDrawer from 'shared/components/filters/FiltersDrawer';
import ColumnSelector from 'shared/components/Table/actions-buttons/ColumnSelector';
import FiltersButton from 'shared/components/Table/FiltersButton';
import RefreshList from 'shared/components/Table/Refresh';
import SearchList from 'shared/components/Table/Search';
import TableToolbar from 'shared/components/Table/TableToolbar';
import StyledAutocomplete from 'shared/components/StyledAutocomplete';
import StyledDatePicker from 'shared/components/StyledDatePicker';
import EventoChipsBar from './EventoChipsBar';
import {
  EventosColumnVisibility,
  EventosFilters,
  eventosColumnLabels
} from '../utils/eventosStorage';

interface Props {
  filters: EventosFilters;
  clients: ClientBusinessPartialModel[];
  columnVisibility: EventosColumnVisibility;
  isLoading: boolean;
  onFiltersChange: (filters: EventosFilters) => void;
  onResetFilters: () => void;
  onColumnVisibilityChange: (columnVisibility: EventosColumnVisibility) => void;
  onRefresh: () => void | Promise<void>;
}

const hasActiveFilters = (filters: EventosFilters) =>
  filters.businessClientId !== null ||
  filters.fromDate !== null ||
  filters.toDate !== null;

const EventoFiltersBar = ({
  filters,
  clients,
  columnVisibility,
  isLoading,
  onFiltersChange,
  onResetFilters,
  onColumnVisibilityChange,
  onRefresh
}: Props) => {
  const [showFilters, setShowFilters] = useState(false);
  const selectedClient =
    clients.find((client) => client.id === filters.businessClientId) ?? null;
  const activeFilters = hasActiveFilters(filters);

  // El debounce vive dentro de SearchList; la ref evita usar filtros viejos
  // si cambiaron mientras se esperaba para buscar.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    onFiltersChange({
      ...filtersRef.current,
      search: trimmed === '' ? null : trimmed
    });
  };

  return (
    <>
      <TableToolbar
        className="bg-page-light dark:bg-page-dark"
        chips={
          <EventoChipsBar
            filters={filters}
            clients={clients}
            onFiltersChange={onFiltersChange}
          />
        }
        actions={
          <>
            <SearchList
              value={filters.search ?? ''}
              onSearch={handleSearch}
              placeholder="Buscar eventos"
            />
            <RefreshList
              onRefreshTable={async () => {
                await onRefresh();
              }}
              isRefreshing={isLoading}
            />
            <ColumnSelector
              columns={columnVisibility}
              onChange={onColumnVisibilityChange}
              columnLabels={eventosColumnLabels}
            />
            <FiltersButton
              onClick={() => setShowFilters(true)}
              isActive={showFilters || activeFilters}
            />
          </>
        }
      />

      <FiltersDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onClear={onResetFilters}
        width="20rem"
      >
        <div className="flex flex-col gap-4">
          <FilterField label="Cliente">
            <StyledAutocomplete<ClientBusinessPartialModel>
              options={clients}
              value={selectedClient}
              getOptionLabel={(client) => client?.fantasyName ?? ''}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  businessClientId: value ? value.id : null
                })
              }
              placeholder="Todos los clientes"
            />
          </FilterField>

          <FilterField label="Desde">
            <StyledDatePicker
              value={filters.fromDate}
              onChange={(value) =>
                onFiltersChange({ ...filters, fromDate: value })
              }
            />
          </FilterField>

          <FilterField label="Hasta">
            <StyledDatePicker
              value={filters.toDate}
              onChange={(value) =>
                onFiltersChange({ ...filters, toDate: value })
              }
              minDate={filters.fromDate}
            />
          </FilterField>
        </div>
      </FiltersDrawer>
    </>
  );
};

export default EventoFiltersBar;
